import React, { useCallback, useEffect, useState } from "react";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import ReturnItemsPart from "./ReturnItemsPart";
import { COLORS } from "rylak-common/src/constants";
import { COMPLAINT_STATUS, ENTITY_SCOPE, ENTITY_TYPE, RETURN_DELAY_STATUS, RETURN_STATUS, SHIPPING_METHOD } from "~/constants";
import ReturnRefundCard from "./ReturnRefundCard";
import TurnrLink from "~/components/links/TurnrLink";
import ExchangeCard from "./ExchangeCard";
import { RETURN_DELAY_STATUS_MAP, ReturnDelayStatusChip } from "~/components/chips/statusChips/ReturnDelayStatusChip.jsx";
import { ReturnStatusChipAction } from "~/components/chips/statusChips/ReturnStatusChip.jsx";
import { ShippingMethodChip } from "~/components/chips/statusChips/ShippingMethodChip.jsx";
import { WithdrawalChip } from "~/components/chips/statusChips/WithdrawalChip.jsx";
import { ServiceTypeChip, isServiceType } from "~/components/chips/statusChips/ServiceTypeChip.jsx";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import { ComplaintStatusChipAction } from "~/components/chips/statusChips/complaintStatusChip/ComplaintStatusChip.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import {
    formatReadableDateAndTimeShort,
    formatReadableDateShort,
    getServiceLoaderInOrder,
    serviceHasError,
    serviceIsLoading,
    serviceIsSilentlyLoading,
    serviceIsSuccessfullyFetched,
    useServiceHook,
} from "rylak-common";
import { fetchCalculatedRefund, fetchEntitiesAll, fetchEntityLogEvents, performEntityAction } from "~/services";
import { SkeletonLoaderWrapper } from "~/components/loading/skeletons/SkeletonLoaderWrapper.jsx";
import ReturnLogEvents from "./ReturnLogEvents";
import { ShipmentCard } from "./ShipmentCard";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import TurnrInput from "~/components/input/TurnrInput.jsx";
import { faPaperPlaneTop } from "@fortawesome/pro-regular-svg-icons";
import UserAvatar from "~/components/userAvatar/UserAvatar.jsx";
import { getLoggedInUser, getUserDataUserId } from "~/cores/appCore";
import { TurnrFontAwesomeIconButtonRound } from "~/components/buttons/icon-buttons/TurnrFontAwesomeIconButton";
import { getTurnrQualityCheckHref } from "~/cores/pageAccessCore";

function LocalLineDivider() {
    return null;
    // return (
    //     <LineDivider
    //         style={{
    //             height: "1px",
    //             width: "100%",
    //         }}
    //     />
    // );
}

function LocalServiceErrorAlert({ serviceData }) {
    return (
        <>
            <ServiceErrorAlert
                serviceData={serviceData}
                fullWidth
                WrapperComponent={({ children }) => (
                    <TurnrGrid item xs={12}>
                        <LocalLineDivider />
                        <div>{children}</div>
                    </TurnrGrid>
                )}
            />
        </>
    );
}

function LocalSkeletonLoader({ loadingMinHeight = "30px", divider = true }) {
    return (
        <TurnrGrid item xs={12}>
            {divider && <LocalLineDivider />}
            <TurnrCardContent>
                <SkeletonLoaderWrapper slim loading={true} loadingMinHeight={loadingMinHeight}></SkeletonLoaderWrapper>
            </TurnrCardContent>
        </TurnrGrid>
    );
}

export default function ReturnCard(props) {
    const { appProps, index, orderReturn, order, returns, reloadData, refundTransactions, refundTransactionsService } = props;

    let { serviceData: returnItemsService, fetchServiceData: triggerReturnItemsService } = useServiceHook({
        appProps,
        fetchRequested: !!orderReturn,
        fetchData: {
            entityType: ENTITY_TYPE.ORDER_ITEM,
            entityScope: ENTITY_SCOPE.FULL,
            returnId: orderReturn?.id,
            sort: "id,asc",
        },
        fetchFn: fetchEntitiesAll,
    });
    let { serviceData: shipmentsService, fetchServiceData: triggerShipmentsService } = useServiceHook({
        appProps,
        fetchRequested: !!orderReturn,
        fetchData: {
            entityType: ENTITY_TYPE.SHIPMENT,
            entityScope: ENTITY_SCOPE.FULL,
            returnId: orderReturn?.id,
        },
        fetchFn: fetchEntitiesAll,
    });
    let { serviceData: exchangeOrdersService, fetchServiceData: triggerExchangeOrdersService } = useServiceHook({
        appProps,
        fetchRequested: !!orderReturn,
        fetchData: {
            entityType: ENTITY_TYPE.EXCHANGE_ORDER,
            entityScope: ENTITY_SCOPE.FULL,
            returnId: orderReturn?.id,
        },
        fetchFn: fetchEntitiesAll,
    });
    let { serviceData: calculatedRefundService, fetchServiceData: triggerCalculatedRefundService } = useServiceHook({
        appProps,
        fetchRequested: !!orderReturn,
        fetchData: {
            orderId: orderReturn?.order?.id,
            returnId: orderReturn?.id,
        },
        fetchFn: fetchCalculatedRefund,
    });

    let { serviceData: returnEntityEventsService, fetchServiceData: triggerReturnEntityEventsService } = useServiceHook({
        appProps,
        fetchRequested: !!orderReturn,
        fetchData: {
            entityType: ENTITY_TYPE.RETURN,
            entityId: orderReturn?.id,
        },
        onReceive: () => setJustCreatedComment(false),
        fetchFn: fetchEntityLogEvents,
    });

    const { isServiceLoadedOrNotFetchedInOrder } = getServiceLoaderInOrder(
        returnItemsService,
        calculatedRefundService,
        exchangeOrdersService,
        refundTransactionsService,
        shipmentsService,
        returnEntityEventsService,
    );

    const reloadDataLocal = useCallback(() => {
        triggerReturnItemsService(null, { silentFetch: true });
        triggerShipmentsService(null, { silentFetch: true });
        triggerExchangeOrdersService(null, { silentFetch: true });
        triggerCalculatedRefundService(null, { silentFetch: true });
        triggerReturnEntityEventsService(null, { silentFetch: true });
        reloadData();
    }, [reloadData, triggerReturnItemsService, triggerShipmentsService, triggerExchangeOrdersService, triggerCalculatedRefundService, triggerReturnEntityEventsService]);

    const delayStatus = orderReturn?.delayStatus;
    const delayType = orderReturn?.delayType;

    const shipments = shipmentsService?.data;
    const exchangeOrders = exchangeOrdersService?.data;

    const calculatedRefund = calculatedRefundService?.data;
    const showCalculatedRefund = calculatedRefund?.totalAmount > 0;

    const [messageInput, setMessageInput] = useState("");
    const [justCreatedComment, setJustCreatedComment] = useState(false);

    let { serviceData: createMessageService, fetchServiceData: createMessageServiceFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.RETURN,
            id: orderReturn?.id,
        },
        fetchFn: performEntityAction,
    });

    const loadingCreateComment = serviceIsLoading(createMessageService);

    const createCommentDisabled = !messageInput?.trim();

    const onCreateCommentClick = useCallback(() => {
        createMessageServiceFn({
            action: {
                type: "RETURN_CREATE_COMMENT",
                data: {
                    userId: getUserDataUserId(appProps),
                    text: messageInput,
                },
            },
        });
        setMessageInput("");
        setJustCreatedComment(true);
    }, [messageInput]);

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(createMessageService)) {
            triggerReturnEntityEventsService(null, { silentFetch: true });
        } else if (serviceHasError(createMessageService)) {
            setJustCreatedComment(false);
        }
    }, [createMessageService]);

    return (
        <TurnrGrid container>
            <TurnrGrid item xs={12}>
                <div
                    style={{
                        // overflow: "hidden",
                        borderRadius: 4,
                        // background: "white",
                        // border: "1px solid lightgray",
                        // boxShadow:
                        //     "0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 2px 0 rgba(63, 63, 68, 0.15)",
                    }}
                >
                    <TurnrGrid container spacing={0.5}>
                        <TurnrGrid item xs={12}>
                            <TurnrCard
                                style={{
                                    borderRadius: 0,
                                    // outline: "1px solid black",
                                    // boxShadow: "0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 2px 0 rgba(63, 63, 68, 0.15)",
                                }}
                            >
                                {!orderReturn ? (
                                    <TurnrCardContent>
                                        <SkeletonLoaderWrapper slim loading={true} loadingMinHeight={"31px"}></SkeletonLoaderWrapper>
                                    </TurnrCardContent>
                                ) : (
                                    <TurnrGrid
                                        container
                                        wrap={"nowrap"}
                                        spacing={1}
                                        p={2}
                                        style={{
                                            backgroundColor: "white",
                                        }}
                                    >
                                        <TurnrGrid container item spacing={1} justifyContent={"space-between"} alignItems={"center"}>
                                            <TurnrGrid item>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gridGap: "15px",
                                                    }}
                                                >
                                                    <div>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "30px",
                                                            }}
                                                        >
                                                            <h5>Return</h5>
                                                        </div>

                                                        <div
                                                            style={{
                                                                whiteSpace: "nowrap",
                                                                fontSize: "12px",
                                                                marginTop: "1px",
                                                                display: "flex",
                                                                gap: "20px",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <span className={"subtitle muted"} style={{ fontSize: "11px" }}>
                                                                #{orderReturn?.id}{" "}
                                                            </span>
                                                            <span className={"subtitle muted"} style={{ fontSize: "11px" }}>
                                                                {formatReadableDateAndTimeShort({ dateTime: orderReturn?.createdAt })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TurnrGrid>
                                            <TurnrGrid item style={{ marginLeft: "auto" }}>
                                                <TurnrGrid container spacing={1}>
                                                    <TurnrGrid item>
                                                        {orderReturn?.returnStatus === RETURN_STATUS.AWAITING_MERCHANT_ACTION ? (
                                                            <ComplaintStatusChipAction
                                                                appProps={appProps}
                                                                status={COMPLAINT_STATUS.PENDING}
                                                                order={order}
                                                                orderReturn={orderReturn}
                                                                size={"small"}
                                                                onSuccess={reloadDataLocal}
                                                            />
                                                        ) : (
                                                            <ReturnStatusChipAction {...props} entity={orderReturn} size={"small"} orderReturn={orderReturn} onSuccess={reloadDataLocal} />
                                                        )}
                                                    </TurnrGrid>
                                                    <TurnrGrid item>
                                                        <ShippingMethodChip
                                                            size={"small"}
                                                            labelFn={({ label }) => {
                                                                if (orderReturn?.returnShipmentType === SHIPPING_METHOD.PICKUP && orderReturn?.requestedPickupDate) {
                                                                    return (
                                                                        <span>
                                                                            {label}: {<b>{formatReadableDateShort({ dateTime: orderReturn?.requestedPickupDate })}</b>}
                                                                        </span>
                                                                    );
                                                                }
                                                                return label;
                                                            }}
                                                            status={orderReturn?.returnShipmentType}
                                                        />
                                                    </TurnrGrid>
                                                    {delayStatus && delayStatus !== RETURN_DELAY_STATUS.NO_DELAY && RETURN_DELAY_STATUS_MAP[delayStatus] && (
                                                        <TurnrGrid item>
                                                            <ReturnDelayStatusChip size={"small"} status={delayStatus} delayType={delayType} />
                                                        </TurnrGrid>
                                                    )}
                                                    {orderReturn?.withdrawal && (
                                                        <TurnrGrid item>
                                                            <WithdrawalChip size={"small"} />
                                                        </TurnrGrid>
                                                    )}
                                                    {isServiceType(orderReturn?.serviceType) && (
                                                        <TurnrGrid item>
                                                            <ServiceTypeChip serviceType={orderReturn?.serviceType} size={"small"} />
                                                        </TurnrGrid>
                                                    )}
                                                </TurnrGrid>
                                            </TurnrGrid>
                                        </TurnrGrid>
                                        <TurnrGrid item>
                                            <EntityActionsDotsMenu appProps={appProps} entity={orderReturn} onSuccess={reloadDataLocal} />
                                        </TurnrGrid>
                                    </TurnrGrid>
                                )}
                                {isServiceLoadedOrNotFetchedInOrder(returnItemsService) ? (
                                    <TurnrCardContent>
                                        <SkeletonLoaderWrapper slim loading={true} loadingMinHeight={"122px"}></SkeletonLoaderWrapper>
                                    </TurnrCardContent>
                                ) : (
                                    <>
                                        <ReturnItemsPart {...props} reloadData={reloadDataLocal} orderReturn={orderReturn} returnItemsService={returnItemsService} />
                                        <ServiceErrorAlert noMargins serviceData={returnItemsService} WrapperComponent={({ children }) => <TurnrCardContent>{children}</TurnrCardContent>} />
                                    </>
                                )}
                            </TurnrCard>
                        </TurnrGrid>

                        {delayStatus === RETURN_DELAY_STATUS.MANUAL_HANDLING ? (
                            <>
                                <LocalLineDivider />
                                <TurnrGrid item xs={12}>
                                    <TurnrCard style={{ borderRadius: 0 }}>
                                        <TurnrCardContent>
                                            <TurnrLink
                                                href={getTurnrQualityCheckHref({
                                                    orderReturn,
                                                })}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: "13px",
                                                    }}
                                                >
                                                    Are there items that have been returned? Mark them here
                                                </span>
                                            </TurnrLink>
                                        </TurnrCardContent>
                                    </TurnrCard>
                                </TurnrGrid>
                            </>
                        ) : null}
                        {isServiceLoadedOrNotFetchedInOrder(exchangeOrdersService) ? (
                            <LocalSkeletonLoader divider={false} />
                        ) : (
                            <>
                                {exchangeOrders?.length ? (
                                    <>
                                        <LocalLineDivider />
                                        <TurnrGrid item xs={12}>
                                            {exchangeOrders?.map((exchangeOrder) => {
                                                return <ExchangeCard key={exchangeOrder?.id} {...props} onUpdate={reloadDataLocal} exchangeOrder={exchangeOrder} />;
                                            })}
                                        </TurnrGrid>
                                    </>
                                ) : null}
                            </>
                        )}
                        <LocalServiceErrorAlert serviceData={exchangeOrdersService} />
                        {isServiceLoadedOrNotFetchedInOrder(refundTransactionsService) ? (
                            <LocalSkeletonLoader />
                        ) : (
                            <>
                                {refundTransactions?.length > 0 || showCalculatedRefund ? (
                                    <>
                                        {refundTransactions?.map((refundTransaction, index) => {
                                            return (
                                                <React.Fragment key={index}>
                                                    <LocalLineDivider />
                                                    <TurnrGrid item xs={12}>
                                                        <ReturnRefundCard
                                                            {...props}
                                                            index={index}
                                                            onUpdate={reloadDataLocal}
                                                            orderReturn={orderReturn}
                                                            nrOfRefunds={refundTransactions.length}
                                                            refundTransaction={refundTransaction}
                                                        />
                                                    </TurnrGrid>
                                                </React.Fragment>
                                            );
                                        })}
                                        {showCalculatedRefund ? (
                                            <>
                                                <LocalLineDivider />
                                                <TurnrGrid item xs={12}>
                                                    <ReturnRefundCard
                                                        {...props}
                                                        index={index}
                                                        onUpdate={reloadDataLocal}
                                                        nrOfRefunds={refundTransactions.length}
                                                        orderReturn={orderReturn}
                                                        refundTransaction={calculatedRefund}
                                                    />
                                                </TurnrGrid>
                                            </>
                                        ) : null}
                                    </>
                                ) : null}
                            </>
                        )}
                        <LocalServiceErrorAlert serviceData={refundTransactionsService} />
                        <LocalServiceErrorAlert serviceData={calculatedRefundService} />
                        {isServiceLoadedOrNotFetchedInOrder(shipmentsService) ? (
                            <LocalSkeletonLoader />
                        ) : (
                            shipments?.length > 0 && (
                                <>
                                    <LocalLineDivider serviceData={shipmentsService} />
                                    <TurnrGrid item xs={12}>
                                        {shipments?.map((shipment, index) => {
                                            return <ShipmentCard key={index} appProps={appProps} onUpdate={reloadDataLocal} shipment={shipment} />;
                                        })}
                                    </TurnrGrid>
                                </>
                            )
                        )}
                        <LocalServiceErrorAlert serviceData={shipmentsService} />
                        {!isServiceLoadedOrNotFetchedInOrder(returnEntityEventsService) && (
                            <TurnrGrid item xs={12}>
                                <TurnrCard
                                    style={{
                                        borderRadius: 0,
                                    }}
                                >
                                    <TurnrCardContent>
                                        <div
                                            style={{
                                                marginTop: "-5px",
                                                marginBottom: "-5px",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <div style={{ marginRight: "10px" }}>
                                                <UserAvatar user={getLoggedInUser(appProps)} />
                                            </div>
                                            <TurnrInput
                                                noLeftBox={true}
                                                fullWidth={true}
                                                debounce={false}
                                                style={{
                                                    maxWidth: "10000px",
                                                }}
                                                inputStyle={{ fontWeight: "400" }}
                                                onChange={setMessageInput}
                                                disabled={loadingCreateComment}
                                                value={messageInput}
                                                fieldsetStyle={{
                                                    borderRadius: "0px",
                                                    display: "none",
                                                }}
                                                onEnter={onCreateCommentClick}
                                                rightIcon={
                                                    <TurnrFontAwesomeIconButtonRound
                                                        disabled={createCommentDisabled}
                                                        onClick={onCreateCommentClick}
                                                        color={COLORS.ACTION}
                                                        style={{
                                                            visibility: createCommentDisabled && "hidden",
                                                        }}
                                                        icon={faPaperPlaneTop}
                                                        size={"medium"}
                                                    />
                                                }
                                                placeholder={"Leave a comment"}
                                            ></TurnrInput>
                                        </div>
                                    </TurnrCardContent>
                                </TurnrCard>
                            </TurnrGrid>
                        )}
                    </TurnrGrid>
                </div>
            </TurnrGrid>
            <TurnrGrid item xs={12}>
                <ReturnLogEvents
                    appProps={appProps}
                    returns={returns}
                    loadingCreateComment={loadingCreateComment || serviceIsSilentlyLoading(returnEntityEventsService) || justCreatedComment}
                    returnEntityEventsService={returnEntityEventsService}
                />
                <ServiceErrorAlert serviceData={returnEntityEventsService} />
            </TurnrGrid>
        </TurnrGrid>
    );
}
